import React, { Fragment } from 'react';
import {
  Button,
  Card,
  Divider,
  Grid,
  Header,
  Icon,
  Label,
  Menu,
  Modal,
  Segment
} from 'semantic-ui-react';
import _ from 'lodash';
import { ImageTypes, isDownloadable } from '../utils/attachmentTypes';
import mapConnection from '../utils/connectionMapper';
import { findDocumentType } from '../utils/documentTypes';
import getTransformedImageName from '../utils/getTransformedImageName'
import DocumentForm from './DocumentForm';
import DocumentPreview from './DocumentPreview';
import DocumentTypeDropdown from './DocumentTypeDropdown';
import { getTransformedImage } from './imageTransformer';
import PassengersInClaimDropdown from './PassengersInClaimDropdown';

const FULL_CROP = { x: 0, y: 0, width: 100.0, height: 100.0 };

class CropImage extends React.Component {
  constructor(props) {
    super(props);
    this.state = { disabledCards: [] };
  }

  handleCropComplete = (document, crop) => {
    getTransformedImage(document.documentUrl, crop, 0, getTransformedImageName(document)).then(
      img => {
        if (!img) {
          return;
        }
        this.props.onCropComplete(img, document);
      }
    );
    this.handleClosePreview();
  };

  handleRotate(document, rotationDelta: integer) {
    getTransformedImage(
      document.documentUrl,
      FULL_CROP,
      rotationDelta,
      getTransformedImageName(document),
    ).then(img => {
      if (!img) {
        return;
      }
      this.props.onDocumentRotated(img, document);
    });
  }

  openDropdown = (document, dropdownType) => {
    const index = document.id + dropdownType;
    this.setState({ dropdownIndex: index });
  };

  closeDropdown = document => {
    this.setState({ dropdownIndex: undefined });
  };

  handleTypeSelected(document, type, e) {
    this.props.onDocumentTypeChanged(type, document);

    this.setState({ dropdownIndex: undefined });
    e.stopPropagation();
  }

  handlePassengerSelected(document, claimPassengerTicket, e) {
    this.props.onDocumentAssignedPassengerChanged(
      claimPassengerTicket,
      document
    );

    this.setState({ dropdownIndex: undefined });
    e.stopPropagation();
  }

  handleDocumentRemoved = document => {
    this.props.onDocumentRemoved(document);
  };

  handleOpenPreview = document => {
    this.setState({
      previewDocument: document,
      previewDocumentValidation: undefined
    });
  };
  handleOpenValidate = document => {
    this.setState({
      previewDocumentValidation: document,
      previewDocument: undefined
    });
  };

  handleClosePreview = () => {
    this.setState({
      previewDocument: undefined,
      previewDocumentValidation: undefined
    });
  };

  handleDocumentCreate = i => {
    this.refs[`fileUploader${i}`].value = null;
    this.refs[`fileUploader${i}`].click();
  };

  handleFileChange = (e, passengerTicketId) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      this.props.onFilesSelected(files, passengerTicketId);
    }
  };

  transformBlobToImageUrl(blob) {
    const urlCreator = window.URL || window.webkitURL;
    return urlCreator.createObjectURL(blob);
  }
  disableFormButton = document => {
    return !(
      document &&
      document.type &&
      findDocumentType(document.type).formComponent
    );
  };
  disableRotation = document => {
    return !ImageTypes.includes(document.attachmentContentType);
  };
  disablePreview = document => {
    return !(
      ImageTypes.includes(document.attachmentContentType) ||
      document.attachmentContentType === 'application/pdf'
    );
  };
  disableDownload = document => {
    return this.props.editDisabled
      ? this.props.editDisabled
      : !isDownloadable(document.attachmentContentType);
  };

  onFileError = cardId => {
    this.setState({ disabledCards: [...this.state.disabledCards, cardId] });
  };

  render = () => (
    <Segment>
      {this.renderDocumentPreviewModal()}
      <Grid columns={1} divided>
        <Grid.Row>
          <Grid.Column width={16}>
            <Grid.Row columns={1}>
              <Grid.Column>{this.renderDocumentCards()}</Grid.Column>
            </Grid.Row>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );

  renderPreviewDocumentValidation = previewDocumentValidation => {
    return (
      <Fragment>
        <DocumentForm
          document={previewDocumentValidation}
          claim={this.props.claim}
          handleClose={this.handleClosePreview}
          onUpdateDocumentSuccess={this.props.onUpdateDocumentSuccess}
        />
        <Divider hidden />
      </Fragment>
    );
  };

  renderDocumentPreviewModal() {
    const previewDocument = this.state.previewDocument;
    const previewDocumentValidation = this.state.previewDocumentValidation;
    const modalContent = previewDocumentValidation ? (
      this.renderPreviewDocumentValidation(previewDocumentValidation)
    ) : previewDocument ? (
      <DocumentPreview
        editDisabled={this.props.editDisabled}
        onCrop={
          previewDocument.attachmentContentType === 'application/pdf'
            ? img => this.props.onCropComplete(img, previewDocument)
            : this.handleCropComplete.bind(this, previewDocument)
        }
        document={previewDocument}
        src={previewDocument.documentUrl}
        onClick={this.handleClosePreview.bind(this)}
      />
    ) : null;
    return (
      <Modal
        open={(previewDocument || previewDocumentValidation) !== undefined}
        dimmer={true}
        closeIcon
        basic={!!previewDocument}
        onClose={this.handleClosePreview.bind(this)}
        closeOnDimmerClick={false}
      >
        <Modal.Content>{modalContent}</Modal.Content>
      </Modal>
    );
  }

  renderDocumentCards() {
    const {
      selectedDocuments,
      errorDocuments,
      passengers,
      updateSelectedDocuments,
      onlyOneDocument,
      disableCropping
    } = this.props;
    return passengers.map((p, i) => {
      const passengerName =
        p.passengerTicket.passengerFirstName +
        ' ' +
        p.passengerTicket.passengerLastName;
      const documentsList = p.documents.map((document, i) => {
        const style = {};
        const { id, type, documentValid } = document;
        if (_.some(selectedDocuments, ['id', id])) {
          style.backgroundColor = 'rgba(33, 186, 69, .2)';
        }
        if (errorDocuments && errorDocuments.includes(id)) {
          style.boxShadow = 'inset 0 0 0 8px rgb(255, 0, 0)';
        }
        if (this.state.disabledCards.includes(id)) {
          return (
            <Card
              key={document.id}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              File isn't available
            </Card>
          );
        }
        return (
          <Card key={id} style={style}>
            {this.renderDocumentBar(document)}
            <Card.Content
              style={{
                height: '25vh',
                overflow: 'hidden',
                textAlign: 'center'
              }}
              onClick={
                updateSelectedDocuments
                  ? () =>
                      updateSelectedDocuments(id, type, documentValid, true)
                  : null
              }
            >
              <DocumentPreview
                document={document}
                type="small"
                onCrop={
                  !disableCropping &&
                  this.handleCropComplete.bind(this, document)
                }
                editDisabled={this.props.editDisabled}
                onFileError={this.onFileError}
              />
            </Card.Content>
            <Card.Content extra>
              {this.renderPassengerAssign(document)}
              {this.renderDocumentType(document, i)}
            </Card.Content>
          </Card>
        );
      });
      return (
        <Fragment key={i}>
          {!onlyOneDocument && <Header>{passengerName}</Header>}

          <Card.Group>
            {!onlyOneDocument && (
              <Card>
                <Button
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                  icon
                  onClick={this.handleDocumentCreate.bind(this, i)}
                >
                  <input
                    type="file"
                    multiple
                    ref={`fileUploader${i}`}
                    style={{ display: 'none' }}
                    onChange={e => {
                      this.handleFileChange(e, `${p.passengerTicket.id}`);
                    }}
                  />,
                  <Icon name="plus" size="massive" />
                </Button>
              </Card>
            )}

            {documentsList}
          </Card.Group>
          {!onlyOneDocument && <Divider />}
        </Fragment>
      );
    });
  }

  renderDocumentBar = document => {
    return (
      <Card.Content extra>
        <Button.Group basic>
          <Button
            icon
            onClick={this.handleDocumentRemoved.bind(this, document)}
            disabled={this.props.editDisabled}
          >
            <Icon name="trash" />
          </Button>
          <Button
            icon
            disabled={this.disableRotation(document)}
            onClick={this.handleRotate.bind(this, document, 3)}
          >
            <Icon name="undo" />
          </Button>
          <Button
            icon
            disabled={this.disableRotation(document)}
            onClick={this.handleRotate.bind(this, document, 1)}
          >
            <Icon name="repeat" />
          </Button>
          <Button
            icon
            disabled={this.disablePreview(document)}
            onClick={this.handleOpenPreview.bind(this, document)}
          >
            <Icon name="maximize" />
          </Button>
          <Button
            icon
            disabled={this.disableFormButton(document)}
            onClick={this.handleOpenValidate.bind(this, document)}
          >
            <Icon name="edit" />
          </Button>
          <Button icon disabled={this.disableDownload(document)}>
            <a href={document.documentUrl}>
              <Icon name="download" />
            </a>
          </Button>
        </Button.Group>
      </Card.Content>
    );
  };

  renderDocumentType = (document, index) => {
    return (
      document && (
        <Menu compact style={{ borderWidth: '0px', boxShadow: '0px 0px' }}>
          {!this.props.editDisabled && (
            <DocumentTypeDropdown
              open={document.id + 'document' === this.state.dropdownIndex}
              onClick={this.openDropdown.bind(this, document, 'document')}
              onBlur={this.closeDropdown.bind(this, document, 'document')}
              onItemSelected={this.handleTypeSelected.bind(this, document)}
            />
          )}
          {document.type
            ? this.renderTypeLabel(
                this.openDropdown.bind(this, document, 'document'),
                document.type
              )
            : this.renderTypeLabel(
                this.openDropdown.bind(this, document, 'document')
              )}
        </Menu>
      )
    );
  };

  renderPassengerAssign = document => {
    if (this.props.editDisabled) return null;
    return (
      document && (
        <Menu
          compact
          style={{
            padding: '0px 0px 5px 0px',
            borderWidth: '0px',
            boxShadow: '0px 0px'
          }}
        >
          <PassengersInClaimDropdown
            open={document.id + 'passenger' === this.state.dropdownIndex}
            onClick={this.openDropdown.bind(this, document, 'passenger')}
            onBlur={this.closeDropdown.bind(this, document, 'passenger')}
            onItemSelected={this.handlePassengerSelected.bind(this, document)}
            options={mapConnection(
              this.props.claim.claimsPassengerTicketConnection
            )}
          />
          {document.claimsPassengerTicket.passengerTicket.id
            ? this.renderPassengerLabel(
                this.openDropdown.bind(this, document, 'passenger'),
                document.claimsPassengerTicket.passengerTicket
              )
            : this.renderPassengerLabel(
                this.openDropdown.bind(this, document, 'passenger')
              )}
        </Menu>
      )
    );
  };

  renderTypeLabel = (onClick, type) => {
    const documentType = findDocumentType(type);
    return (
      <Label color={documentType.color || 'grey'} onClick={onClick}>
        {documentType.text || 'Unassigned'}
      </Label>
    );
  };

  renderPassengerLabel = (onClick, passenger) => {
    return (
      <Label color={'orange'} onClick={onClick}>
        {passenger
          ? passenger.passengerFirstName + ' ' + passenger.passengerLastName
          : 'Unsingned'}
      </Label>
    );
  };
}

export default CropImage;



// WEBPACK FOOTER //
// ./src/component/CropImage.js